// src/components/informatie/RelatedArticles.tsx
// Gerelateerde artikelen component voor internal linking
// WFT Study Buddy - SEO Internal Linking Strategy

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Article } from '../../services/contentService';

interface RelatedArticlesProps {
  currentArticleId: string;
  category: string;
  examType?: string;
  keywords?: string[];
  limit?: number;
}

// Category styling (light theme)
const categoryStyles: Record<string, { bg: string; text: string; icon: string }> = {
  examens: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    icon: '📋'
  },
  begrippen: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    icon: '📚'
  },
  studietips: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    icon: '💡'
  },
  statistieken: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    icon: '📊'
  },
};

const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  currentArticleId,
  category,
  examType,
  keywords = [],
  limit = 4
}) => {
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedArticles = async () => {
      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
        
        // Fetch related articles from backend
        const response = await fetch(
          `${backendUrl}/api/content/articles/related?` + 
          `currentId=${currentArticleId}&` +
          `category=${category}&` +
          `examType=${examType || ''}&` +
          `keywords=${keywords.join(',')}&` +
          `limit=${limit}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setRelatedArticles(data.articles || []);
        }
      } catch (error) {
        console.error('Error fetching related articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedArticles();
  }, [currentArticleId, category, examType, keywords, limit]);

  if (loading) {
    return (
      <div className="mt-12 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-50 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (relatedArticles.length === 0) {
    return null;
  }

  return (
    <section className="mb-8">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10">
          <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900">
          Gerelateerde Artikelen
        </h2>
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {relatedArticles.map((article) => {
          const style = categoryStyles[article.category] || categoryStyles.begrippen;

          return (
            <Link
              key={article.id}
              to={`/informatie/${article.category}/${article.slug}`}
              className="group relative overflow-hidden rounded-xl
                         bg-white/70 backdrop-blur-sm
                         border border-white/50 shadow-sm
                         hover:shadow-md hover:border-blue-200
                         transition-all duration-300
                         p-5"
            >
              {/* Category Badge */}
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                              text-xs font-medium mb-3
                              ${style.bg} ${style.text}`}>
                <span>{style.icon}</span>
                <span className="capitalize">{article.category}</span>
              </div>

              {/* Title */}
              <h3 className="text-gray-900 font-semibold text-base mb-2
                           group-hover:text-blue-600 transition-colors
                           line-clamp-2">
                {article.title}
              </h3>

              {/* Meta Description Preview */}
              <p className="text-gray-500 text-sm line-clamp-2">
                {article.metaDescription}
              </p>

              {/* Arrow Icon */}
              <div className="absolute bottom-4 right-4
                            opacity-0 group-hover:opacity-100
                            transform translate-x-2 group-hover:translate-x-0
                            transition-all duration-300">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </Link>
          );
        })}
      </div>

      {/* View More Link */}
      <div className="mt-6 text-center">
        <Link
          to={`/informatie/${category}`}
          className="inline-flex items-center gap-2 text-sm text-gray-500
                   hover:text-blue-600 transition-colors"
        >
          Bekijk alle {category} artikelen
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </section>
  );
};

export default RelatedArticles;